<template>
  <main class="report-source">
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave"
    />
    <div class="report-source__header">
      <h1 class="report-source__title">
        Стоимость привлечения пациента
      </h1>
      <form
        class="report-source__filters"
        v-if="hasFetchedOptions"
        v-on:submit.prevent
      >
        <date-range-with-presets
          v-bind:presets="presetsOfDates"
          v-bind:value="selectedDates"
          custom-color-date="black"
          v-on:input="onInputDates" />
        <multiple-autocomplete-input
          label="Выберите источники"
          multiple
          removal-per-chip
          select-all-text="Все источники"
          set-select-all-option
          small-chips
          v-bind:items="sourceOptions"
          v-bind:value="selectedSources"
          v-on:input="onInputSources"
        />
        <input-autocomplete
          title=""
          placeholder="Пациенты"
          v-bind:options="patientFilterOptions"
          v-bind:value="selectedPatientFilter"
          v-on:input="onInputPatientFilter"
          class="report-source__autocomplete"
        />
      </form>
      <collapse-button caption="Внести расходы">
        <div class="report-source__expenditures">
          <table class="report-source-table report-source__expenditures__sources">
            <thead class="report-source-table__head-line">
              <tr>
                <th
                  class="report-source-table__head-cell"
                >Источники</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="report-source-table__body-line"
                v-for="row in channels.rows"
                v-bind:key="row.id"
              >
                <td class="report-source-table__body-cell">{{ row.name }}</td>
              </tr>
            </tbody>
          </table>
          <table
            class="report-source-table report-source__expenditures__data"
            :style="reportDataTableStyles"
          >
            <thead class="report-source-table__head-line">
              <tr>
                <th
                  class="report-source-table__head-cell"
                  v-for="{ field } in channels.headers"
                  v-bind:key="field"
                >{{ getYearAndMonthTitle(field) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="report-source-table__body-line report-source-table__body-line--editable"
                v-for="row in channels.rows"
                v-bind:key="row.id"
              >
                <td
                  class="report-source-table__body-cell"
                  v-for="(amount, index) in row.amounts"
                  v-bind:key="index"
                >
                  <price-input
                    class="report-source-table__price-input"
                    v-model="row.amounts[index]"
                    v-on:input="debounceExpendituresChanged"
                    v-bind:with-currency="false"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </collapse-button>
      <div class="report-source__buttons">
        <button
          type="button"
          v-on:click="onUpdateExpenditures"
          class="report-source__button">Сформировать</button>
        <button
          type="button"
          v-on:click="fetchReportSpreadsheet"
          class="report-source__button excel">EXCEL</button>
      </div>
    </div>
    <div class="report-source__body">
      <table class="report-source-table report-source__table-details">
        <thead class="report-source-table__head-line">
          <tr>
            <th
              class="report-source-table__head-cell"
              v-bind:key="title"
              v-for="({ title, sorts }) in columns"
            >
              <sort-by-column
                class="report-source-table__sort"
                v-bind:items="sorts"
                v-bind:label="title"
                v-bind:value="sort"
                v-if="sorts.length"
                v-on:input="onInputSort" />
              <template v-else>{{ title }}</template>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="report-source-table__body-line"
            v-bind:key="`table-rows:${rowIndex}`"
            v-for="(row, rowIndex) in rows"
          >
            <td
              class="report-source-table__body-cell"
              v-bind:key="`table-rows:${rowIndex}:cells:${cellIndex}`"
              v-for="(title, cellIndex) in row"
            >
              <span>{{ title }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>
<script>
import CollapseButton from '@/components/CollapseButton.vue';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import debounce from 'lodash.debounce';
import saveFileMixin from '@/mixins/saveFileMixin';
import dateMixin, { getISODateStringByDate } from '@/mixins/dateMixin';
import PriceInput from '@/components/PriceInput.vue';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import InputAutocomplete from '@/components/InputAutocomplete.vue';
import notifierMixin from '@/mixins/notifierMixin';

/**
 * @description Компонент сортировки по столбцу.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const head = this.sortedItems[0];
      return head ? head.title : '';
    },
    isNotSelected() {
      return this.items.some(({ value }) => value === this.value) !== true;
    },
    sortedItems() {
      const items = [
        {
          checked: this.isNotSelected,
          title: 'Без сортировки',
          value: '',
        },
        ...this.items.map(({ title, value }) => ({
          checked: value === this.value,
          title,
          value,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  render(createElement) {
    const name = `report-sort-${this.title}`;
    return createElement('label', { attrs: { title: this.headTitle }, class: !this.isNotSelected ? 'selected' : '' }, [
      createElement('span', this.label).children[0],
      ...this.sortedItems.map(({ checked, title, value }) => createElement('input', {
        attrs: {
          'aria-label': title,
          checked,
          name,
          title,
          type: 'radio',
        },
        domProps: { checked },
        key: `column:${this.label}:sort:${value}`,
        on: { input: () => { this.$emit('input', value); } },
      })),
    ]);
  },
  props: {
    items: {
      required: true,
      type: Array,
    },
    label: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
};

/**
 * Часть названия отчета: Отчет по стоимости привлечения пациента
 *
 * @type {string}
 */
const PART_OF_REPORT_REPORT_BY_PATIENT_ATTRACTING_COST = 'Отчет по стоимости привлечения пациента';

/**
 * @description Отчет по стоимости привлечения пациента.
 * @type {Vue}
 */
export default {
  components: {
    CollapseButton,
    DateRangeWithPresets,
    InputAutocomplete,
    ModalDialogPreventLeave,
    MultipleAutocompleteInput,
    PriceInput,
    SortByColumn,
  },
  computed: {
    /**
     * @description Набор предварительных дат для фильтрации.
     * @returns {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
    /**
     * @description Предостращать уход со страницы.
     * @returns {boolean}
     */
    needPreventLeave() {
      return this.isExpendituresChanged;
    },
    /**
     * @description Стили для таблицы расходов.
     * @returns {object}
     */
    reportDataTableStyles() {
      const countCol = this.channels.headers?.length;
      return {
        minWidth: `${countCol * 115}px`,
      };
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this
          .presetsOfDates
          .slice(0, 1)
          .forEach((candidate) => {
            this.selectedDates = candidate.toValue();
          });
        this
          .patientFilterOptions
          .slice(0, 1)
          .forEach(({ value }) => {
            this.selectedPatientFilter = value;
          });
        this.selectedSources = this.sourceOptions.map(({ value }) => value);
        this.fetchReport();
        this.fetchExpendituresByPeriod();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      /** Наименование филиала клиники * */
      branchName: '',
      channels: [],
      columns: [],
      hasFetchedOptions: false,
      initialExpendituresState: '',
      isExpendituresChanged: false,
      isFinishedReport: false,
      periodOptions: [],
      patientFilterOptions: [],
      reportId: null,
      rows: [],
      selectedDates: [],
      selectedSources: [],
      selectedPatientFilter: '',
      sort: 'by-patient-cost-desc',
      sourceOptions: [],
      todayDates: [],
    };
  },
  methods: {
    calcExpendituresMomentState() {
      return JSON.stringify(this.channels.rows);
    },
    debounceExpendituresChanged: debounce(function _() {
      const state = this.calcExpendituresMomentState();
      this.isExpendituresChanged = state !== this.initialExpendituresState;
    }, 300),
    debounceFetchReport: debounce(function _() { this.fetchReport(); }, 1000),
    /**
     * @description Получить опции отчета.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$reportGateway
        .getPatientAttractingCostReportOptions()
        .then((result) => {
          result.forEach(({
            patient_filters: patientFilters,
            period_types: periods,
            sources,
            branch_name: branchName,
          }) => {
            this.periodOptions = periods;
            this.sourceOptions = sources;
            this.patientFilterOptions = patientFilters;
            this.branchName = branchName;
          });
        });
    },
    /**
     * @description Получить расходы.
     * @returns {void}
     */
    async fetchExpendituresByPeriod() {
      if (this.isExpendituresChanged) {
        const dialog = await this.showDialog(
          'Расходы небыли сохранены. Продолжить?',
          DialogResult.NO,
          DialogResult.YES,
        );
        if (dialog.result === DialogResult.NO) {
          return;
        }
      }
      const [dateForm, dateTo] = this.selectedDates.map(getISODateStringByDate);
      const onSuccess = (response) => {
        this.channels = response;
        this.initialExpendituresState = this.calcExpendituresMomentState();
        this.isExpendituresChanged = false;
      };
      const onFail = (error) => this.notifyFail(error.getTitle());
      this
        .$advertisingChannelGateway
        .viewExpendituresByPeriod(dateForm, dateTo)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить данные отчета.
     * @returns {void}
     */
    async fetchReport() {
      this.isFinishedReport = false;
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      this
        .$reportGateway
        .getPatientAttractingCostReport(
          dateFrom,
          dateTo,
          this.selectedSources,
          this.sort,
          this.selectedPatientFilter,
        )
        .then((result) => {
          result.forEach(({ columns, report_id: reportId, rows }) => {
            this.columns = columns;
            this.reportId = reportId;
            this.rows = rows;
          });
        })
        .finally(() => { this.isFinishedReport = true; });
    },
    /**
     * @description Получить отчет в виде таблицы.
     * @returns {void}
     */
    fetchReportSpreadsheet() {
      const reportName = this.getReportFileName();
      const onSuccess = (response) => this
        .saveFile(response, reportName);
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$reportGateway
        .getPatientAttractingCostReportBySheet(this.reportId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Сформировать название отчета для файла.
     * @returns {string}
     */
    getReportFileName() {
      const parts = [];
      parts.push(PART_OF_REPORT_REPORT_BY_PATIENT_ATTRACTING_COST);
      if (this.branchName && this.branchName.trim() !== '') {
        parts.push(this.branchName.trim());
      }
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      parts.push(`${dateFrom} — ${dateTo}`);
      const fileName = parts
        .filter((part) => part && part.trim() !== '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      return `${fileName}.xls`;
    },
    /**
     * @description Обработать выбор периода для отчета.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.fetchExpendituresByPeriod();
        this.debounceFetchReport();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать выбор фильтра по пациентам.
     * @param {string} value
     * @returns {void}
     */
    onInputPatientFilter(value) {
      this.selectedPatientFilter = value;
      this.debounceFetchReport();
    },
    /**
     * @description Обработать ввод источников.
     * @param {string[]} value
     * @returns {void}
     */
    onInputSources(value) {
      this.selectedSources = value;
      this.debounceFetchReport();
    },
    /**
     * @description Обработать ввод сортировки.
     * @param {string} value
     * @returns {void}
     */
    onInputSort(value) {
      this.sort = value;
      this.debounceFetchReport();
    },
    /**
     * @description Обработать обновление расходов на источники.
     * @returns {void}
     */
    onUpdateExpenditures() {
      const onSuccess = (title) => {
        this.notifySuccess(title);
        this.initialExpendituresState = this.calcExpendituresMomentState();
        this.isExpendituresChanged = false;
        this.fetchReport();
      };
      const onFail = (error) => this.notifyFail(error.getTitle());
      this
        .sendUpdateExpendituresRequest()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Сохранить и продолжить.
     * @returns {void}
     */
    async saveAndLeave() {
      this
        .sendUpdateExpendituresRequest()
        .then(this.actionAfterClosingDialogLeaveConfirm)
        .finally(() => { this.isShownDialogLeaveConfirm = false; });
    },
    /**
     * @description Отправить запрос на обновление расходов по источникам.
     * @returns {Promise<Either>}
     */
    sendUpdateExpendituresRequest() {
      const months = this.channels.headers.map(({ field }) => field);
      const channels = this.channels.rows.map((channel) => {
        const expenditures = channel.amounts.map((amount, index) => {
          const month = months[index];
          return { amount, month };
        });
        return { id: channel.id, expenditures };
      });
      return this
        .$advertisingChannelGateway
        .updateExpenditures(channels);
    },
  },
  mixins: [
    builtInDialogMixin,
    notifierMixin,
    preventLeaveWithoutConfirmMixin,
    saveFileMixin,
    dateMixin,
  ],
  name: 'ClinicPanelReportPatientAttractingCost',
};
</script>
<style lang="scss">
@use "@/scss/buttons";

.report-source {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__autocomplete {
    .v-text-field__details {
      min-height: auto;
    }
    &.v-input input {
      max-height: 48px;
    }
    &.v-text-field input {
      line-height: 26px;
    }
  }
  &__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: var(--section-background-color);
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__buttons {
    display: flex;
    gap: 10px;
    margin: 40px 0;
  }
  &__button {
    @include buttons.btn-transparent(155px, 48px, 16px);
    &.excel::before {
      content: '';
      background: no-repeat 50%;
      background-image: url("@/assets/img/exel.svg");
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
  &__expenditures {
    overflow-x: auto;
    padding-bottom: 10px;
    display: flex;
    &__sources {
      min-width: 350px;
    }
    &__data {
      min-width: 200px;
    }
  }
  &__sources {
    position: sticky;
    left: 0;
    background: var(--section-background-color);
  }
  &__filters {
    align-items: baseline;
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }
  &__body {
    padding: 38px 20px 48px 23px;
  }
  &-table {
    width: 100%;
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    &__body-cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
    }
    &__body-line {
      border-bottom: 1px solid var(--fill-color);
      &--editable {
        color: var(--action-color);
      }
    }
    &__head-cell {
      width: max-content;
      padding: 10px 8px;
      text-align: left;
    }
    &__head-line {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
    }
    &__price-input {
      max-width: 80px;
    }
    &__sort {
      display: flex;
      column-gap: 4px;
      align-items: center;
      cursor: pointer;
      input {
        display: none;
      }
      &::after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      &.selected {
        color: var(--action-color);
      }
    }
  }

}
@media screen and (max-width: 1900px) {
  .report-source {
    &__body {
      overflow: scroll;
    }
    &__table-details {
      width: 1900px;
    }
  }
}
@media screen and (max-width: 1500px) {
  .report-source {
    &__filters {
      flex-direction: column;
    }
  }
}
</style>
